import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import './index.css';

/**
 * The entry point.
 *
 * ## Why StrictMode is on, including in production
 *
 * `StrictMode` double-invokes effects in development to surface cleanup bugs —
 * which is how the abort controllers in `useAsync` and `usePagedList` were
 * verified rather than assumed. Leaving it on in production costs nothing: it is
 * compiled away in the release build of React, and having it only in development
 * means the double-invocation path is the one nobody runs.
 *
 * ## Why there is no error boundary here
 *
 * There should be, and the reason there is not is that an error boundary at the
 * root can only render a generic "something went wrong" — which is what the
 * browser shows anyway. The failures worth catching are the per-page ones, and
 * those are handled where they happen: every request in this console returns an
 * `ActionResult` rather than throwing into the render tree, so there is no
 * render-time throw left for a boundary to catch. See docs/ARCHITECTURE.md.
 */
const container = document.getElementById('root');

if (!container) {
  // Not a recoverable condition: the HTML shell is ours and always contains this
  // element. Throwing with the id in the message is more useful than a silent
  // blank page.
  throw new Error('index.html is missing <div id="root">; the console cannot mount');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
